<script setup lang="ts">
import { useApiFetch } from "~/composible/useApiFetch";
import { defineBreadcrumb } from "#imports";
import { format } from 'date-fns';

const appConfig = useAppConfig();
const route = useRoute();

const { data, error, status} = await useApiFetch(`/blog/${route.params.slug}`);

if (error.value?.statusCode === 404) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const { data: recent } = await useApiFetch(`/blog/recent?exclude[]=${data.value.post.id}`, {
  transform: (recent) => recent.data
});

const crumbs = [
  {link: '/blog', label: 'Blog'},
  {label: data.value.post.title }
];

const baseTitle = data.value.post.title;
const baseDescription = data.value.post.description.substring(0, 155);

useSeoMeta({
  titleTemplate: '%s – Read on FictionMe',
  title: baseTitle,
  description: baseDescription,
  ogTitle: baseTitle,
  ogDescription: baseDescription,
  twitterTitle: baseTitle,
  twitterDescription: baseDescription,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      defineListItem({
        position: 1,
        name: 'Blog',
        item: '/blog'
      }),
      defineListItem({
        position: 2,
        name: data.value.post.title,
      }),
    ]
  }),
  defineArticle({
    headline: data.value.post.title,
    description: data.value.post.description,
    datePublished: data.value.post.created_at,
    dateModified:data.value.post.updated_at,
    author: definePerson({
      name: data.value.post.author
    }),
    publisher: defineOrganization({
      name: "FictionMe",
      url: "https://fictionme.net"
    }),
  })
]);
</script>

<template>
    <div class="container py-8">
      <div class="page-content flex flex-col gap-8">
        <breadcrumbs :breadcrumbs="crumbs" />

        <div class="grid grid-cols-[1fr_272px] gap-7 max-lg:grid-cols-1">
          <div class="flex flex-col gap-4 max-md:gap-2">
            <lazy-f-card>
              <div class="flex flex-col gap-8 max-md:gap-4">
                <h1 class="text-[40px] leading-[42px] font-semibold max-md:text-2xl max-md:leading-6">{{ data.post.title }}</h1>

                <div class="flex font-light text-lg">
                  <div>
                    By <span class="text-primary font-semibold">{{ data.post.author }}</span>
                  </div>
                  <div class="ml-4 font-light text-zinc-500 dark:text-gray-400">{{ format(new Date(data.post.created_at), 'MMM dd, yyyy') }}</div>
                </div>

                <post-content-block :html="data.post.content" :books="data.post.books || []" />
              </div>
            </lazy-f-card>

            <div class="flex justify-between">
              <nuxt-link v-if="data.meta.prev" :to="`/blog/${data.meta.prev}`" class="flex items-center font-semibold text-violet-800 dark:text-violet-500 dark:hover:text-violet-600">
                <icon name="fm:arrow-right" class="mr-1 text-xl rotate-180" />
                Previous Post
              </nuxt-link>
              <span v-else></span>
              <nuxt-link v-if="data.meta.next" :to="`/blog/${data.meta.next}`" class="flex items-center font-semibold text-violet-800 dark:text-violet-500 dark:hover:text-violet-600">
                Next Post
                <icon name="fm:arrow-right" class="ml-1 text-xl" />
              </nuxt-link>
              <span v-else></span>
            </div>

            <div class="flex max-md:flex-col gap-8 max-md:gap-4 text-white p-8 max-md:p-4 items-center rounded-xl max-md:text-center" style="background-image: linear-gradient(to right, #4C1CB4 100%, #501592 100%);">
              <div class="space-y-6 max-md:space-y-3">
                <p class="text-3xl max-md:text-2xl">Start your writing career with us!</p>
                <p class="text-2xl max-md:text-lg font-light">We are making a commitment to always be there for you throughout your journey as an author.</p>
              </div>

              <nuxt-link :to="appConfig.authorCabinetUrl" class="py-3 px-8 max-md:w-full bg-white !text-zinc-900 text-nowrap rounded text-lg font-semibold hover:bg-gray-100">
                Join us
              </nuxt-link>
            </div>
          </div>
          <div class="flex flex-col">
            <h3 class="text-2xl font-semibold mb-2 lg:hidden">Recent Articles</h3>
            <div class="grid grid-cols-1 max-lg:grid-cols-2 gap-4">
              <post-preview
                  v-for="post in recent"
                  :post="post"
                  :key="post.id"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<style scoped>

</style>